/**
 * Curve generation algorithms for pipe paths
 */

import type * as THREE_NS from 'three';

export type CurveType = 'bezier' | 'natural-cubic' | 'hermite' | 'b-spline';

export interface Curve {
  getPoint(t: number, target?: THREE_NS.Vector3): THREE_NS.Vector3;
  getTangent?(t: number, target?: THREE_NS.Vector3): THREE_NS.Vector3;
}

/**
 * Create a Natural Cubic Spline curve (minimizes curvature, very stable for offsets)
 */
export function createNaturalCubicSpline(
  points: THREE_NS.Vector3[],
  THREE: typeof THREE_NS
): Curve | null {
  const { Vector3 } = THREE;
  const n = points.length - 1;
  if (n < 1) return null;

  // Solve tridiagonal system for second derivatives
  const h: number[] = [];
  const alpha: THREE_NS.Vector3[] = [];
  const l: number[] = [];
  const mu: number[] = [];
  const z: THREE_NS.Vector3[] = [];
  const c: THREE_NS.Vector3[] = [];
  const d: THREE_NS.Vector3[] = [];
  const b: THREE_NS.Vector3[] = [];

  // Calculate h[i] = x[i+1] - x[i] (parameter spacing)
  for (let i = 0; i < n; i++) {
    h[i] = 1.0 / n; // Uniform spacing
  }

  // Calculate alpha (right-hand side of system)
  for (let i = 1; i < n; i++) {
    const p1 = points[i - 1];
    const p2 = points[i];
    const p3 = points[i + 1];
    alpha[i] = new Vector3()
      .addScaledVector(p3, 3 / h[i])
      .addScaledVector(p2, -3 / h[i] - 3 / h[i - 1])
      .addScaledVector(p1, 3 / h[i - 1]);
  }

  // Forward elimination
  l[0] = 1;
  mu[0] = 0;
  z[0] = new Vector3(0, 0, 0);

  for (let i = 1; i < n; i++) {
    l[i] = 2 * (h[i - 1] + h[i]) - h[i - 1] * mu[i - 1];
    mu[i] = h[i] / l[i];
    const temp = new Vector3().copy(z[i - 1]).multiplyScalar(h[i - 1]);
    z[i] = new Vector3()
      .subVectors(alpha[i], temp)
      .multiplyScalar(1 / l[i]);
  }

  l[n] = 1;
  z[n] = new Vector3(0, 0, 0);
  c[n] = new Vector3(0, 0, 0);

  // Back substitution
  for (let j = n - 1; j >= 0; j--) {
    const temp = new Vector3().copy(c[j + 1]).multiplyScalar(mu[j]);
    c[j] = new Vector3().subVectors(z[j], temp);
    b[j] = new Vector3()
      .subVectors(points[j + 1], points[j])
      .multiplyScalar(1 / h[j])
      .subScaledVector(c[j], h[j] / 3)
      .subScaledVector(c[j + 1], h[j] / 6);
    d[j] = new Vector3()
      .subVectors(c[j + 1], c[j])
      .multiplyScalar(1 / (3 * h[j]));
  }

  // Return curve object with getPoint method
  return {
    getPoint: (t: number, target?: THREE_NS.Vector3): THREE_NS.Vector3 => {
      const result = target || new Vector3();
      t = Math.max(0, Math.min(1, t));
      const i = Math.min(Math.floor(t * n), n - 1);
      const localT = (t * n) - i;
      const localT2 = localT * localT;
      const localT3 = localT2 * localT;

      result.copy(points[i])
        .addScaledVector(b[i], localT)
        .addScaledVector(c[i], localT2)
        .addScaledVector(d[i], localT3);

      return result;
    },
    getTangent: (t: number, target?: THREE_NS.Vector3): THREE_NS.Vector3 => {
      const result = target || new Vector3();
      t = Math.max(0, Math.min(1, t));
      const i = Math.min(Math.floor(t * n), n - 1);
      const localT = (t * n) - i;
      const localT2 = localT * localT;

      result.copy(b[i])
        .addScaledVector(c[i], 2 * localT)
        .addScaledVector(d[i], 3 * localT2)
        .normalize();

      return result;
    },
  };
}

/**
 * Create a Hermite Spline curve (direct tangent control)
 */
export function createHermiteSpline(
  p0: THREE_NS.Vector3,
  t0: THREE_NS.Vector3, // Start point and tangent
  p1: THREE_NS.Vector3,
  t1: THREE_NS.Vector3, // End point and tangent
  THREE: typeof THREE_NS
): Curve {
  const { Vector3 } = THREE;
  return {
    getPoint: (t: number, target?: THREE_NS.Vector3): THREE_NS.Vector3 => {
      const result = target || new Vector3();
      t = Math.max(0, Math.min(1, t));
      const t2 = t * t;
      const t3 = t2 * t;
      const h1 = 2 * t3 - 3 * t2 + 1;
      const h2 = -2 * t3 + 3 * t2;
      const h3 = t3 - 2 * t2 + t;
      const h4 = t3 - t2;

      result.copy(p0).multiplyScalar(h1)
        .addScaledVector(p1, h2)
        .addScaledVector(t0, h3)
        .addScaledVector(t1, h4);

      return result;
    },
    getTangent: (t: number, target?: THREE_NS.Vector3): THREE_NS.Vector3 => {
      const result = target || new Vector3();
      t = Math.max(0, Math.min(1, t));
      const t2 = t * t;
      const dh1 = 6 * t2 - 6 * t;
      const dh2 = -6 * t2 + 6 * t;
      const dh3 = 3 * t2 - 4 * t + 1;
      const dh4 = 3 * t2 - 2 * t;

      result.copy(p0).multiplyScalar(dh1)
        .addScaledVector(p1, dh2)
        .addScaledVector(t0, dh3)
        .addScaledVector(t1, dh4)
        .normalize();

      return result;
    },
  };
}

/**
 * Create a simple B-Spline curve (uniform, degree 3)
 */
export function createBSpline(
  controlPoints: THREE_NS.Vector3[],
  THREE: typeof THREE_NS,
  degree: number = 3
): Curve {
  const { Vector3 } = THREE;
  const n = controlPoints.length;
  const knots: number[] = [];

  // Create uniform knot vector
  for (let i = 0; i < n + degree + 1; i++) {
    knots[i] = i;
  }

  // B-spline basis function
  function basisFunction(i: number, k: number, t: number): number {
    if (k === 0) {
      return (t >= knots[i] && t < knots[i + 1]) ? 1 : 0;
    }

    let denom1 = knots[i + k] - knots[i];
    let denom2 = knots[i + k + 1] - knots[i + 1];

    let term1 = 0;
    let term2 = 0;

    if (denom1 !== 0) {
      term1 = ((t - knots[i]) / denom1) * basisFunction(i, k - 1, t);
    }
    if (denom2 !== 0) {
      term2 = ((knots[i + k + 1] - t) / denom2) * basisFunction(i + 1, k - 1, t);
    }

    return term1 + term2;
  }

  // Normalize t to knot range
  const tMin = knots[degree];
  const tMax = knots[n];
  const tRange = tMax - tMin;

  const curve = {
    getPoint: (t: number, target?: THREE_NS.Vector3): THREE_NS.Vector3 => {
      const result = target || new Vector3();
      t = Math.max(0, Math.min(1, t));
      const tNormalized = tMin + t * tRange;

      result.set(0, 0, 0);
      for (let i = 0; i < n; i++) {
        const basis = basisFunction(i, degree, tNormalized);
        result.addScaledVector(controlPoints[i], basis);
      }

      return result;
    },
  };

  return {
    getPoint: curve.getPoint,
    getTangent: (t: number, target?: THREE_NS.Vector3): THREE_NS.Vector3 => {
      // Approximate tangent using nearby points
      const t1 = Math.max(0, t - 0.01);
      const t2 = Math.min(1, t + 0.01);
      const p1 = curve.getPoint(t1, new Vector3());
      const p2 = curve.getPoint(t2, new Vector3());
      const result = target || new Vector3();
      result.subVectors(p2, p1).normalize();
      return result;
    },
  };
}
